import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, X } from 'lucide-react';
import { Button } from './ui/button';

const SCAN_COOLDOWN_MS = 2000;

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 1200;
    gain.gain.value = 0.3;
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
    osc.onended = () => ctx.close();
  } catch (e) { /* audio indisponível */ }
};

export const BarcodeScannerDialog = ({ open, onOpenChange, onScan }) => {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const lastScanRef = useRef({ code: '', time: 0 });
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [error, setError] = useState('');
  const [lastCode, setLastCode] = useState('');

  const stopScanner = useCallback(() => {
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch (e) { /* já parado */ }
      controlsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      stopScanner();
      setError('');
      setLastCode('');
      return;
    }

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E, BarcodeFormat.CODE_128, BarcodeFormat.CODE_39,
      BarcodeFormat.ITF, BarcodeFormat.QR_CODE
    ]);
    const reader = new BrowserMultiFormatReader(hints);
    let cancelled = false;

    const start = async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result) => {
            if (!result) return;
            const code = result.getText();
            const now = Date.now();
            if (code === lastScanRef.current.code && now - lastScanRef.current.time < SCAN_COOLDOWN_MS) return;
            lastScanRef.current = { code, time: now };
            setLastCode(code);
            playBeep();
            if (navigator.vibrate) navigator.vibrate(100);
            onScanRef.current(code);
          }
        );
        if (cancelled) { controls.stop(); return; }
        controlsRef.current = controls;
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          setError('Permissão da câmera negada. Autorize o acesso à câmera nas configurações do navegador.');
        } else if (err.name === 'NotFoundError') {
          setError('Nenhuma câmera encontrada neste dispositivo.');
        } else {
          setError('Não foi possível abrir a câmera. Verifique se outro app não está usando-a.');
        }
      }
    };

    start();
    return () => { cancelled = true; stopScanner(); };
  }, [open, onScan, stopScanner]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0" aria-describedby={undefined} data-testid="barcode-scanner-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera size={18} />
            Escanear Código de Barras
          </DialogTitle>
        </DialogHeader>
        <div className="relative bg-black">
          {error ? (
            <div className="p-8 text-center text-white text-sm" data-testid="scanner-error">{error}</div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="w-full h-64 object-cover"
                muted
                playsInline
                data-testid="scanner-video"
              />
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-4/5 h-24 border-2 border-emerald-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
              </div>
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none flex justify-center">
                <div className="w-3/4 h-0.5 bg-red-500 animate-pulse" />
              </div>
            </>
          )}
        </div>
        <div className="p-4 space-y-2">
          {lastCode && (
            <p className="text-xs text-center text-emerald-600 font-mono" data-testid="scanner-last-code">
              Último código: {lastCode}
            </p>
          )}
          <p className="text-xs text-center text-slate-500">
            Aponte a câmera para o código de barras. O produto será adicionado automaticamente.
          </p>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => onOpenChange(false)}
            data-testid="scanner-close-btn"
          >
            <X size={16} className="mr-2" />
            Fechar Scanner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
